import React, { useState } from 'react';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isAfter, isWithinInterval } from 'date-fns';
import { FaChevronLeft, FaChevronRight, FaCalendarAlt } from 'react-icons/fa';
import './ReportCalendar.scss';

const ReportCalendar = ({ selectedRange, onRangeChange, submittedWeeks }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Get the current week (Monday to Sunday)
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  
  // Helper to get Monday of the current week view
  const getWeekStart = (date) => startOfWeek(date, { weekStartsOn: 1 });
  
  // Helper to get Sunday of the current week view
  const getWeekEnd = (date) => endOfWeek(date, { weekStartsOn: 1 });
  
  // Navigate to previous week
  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
    const prevWeekStart = getWeekStart(subWeeks(currentDate, 1));
    const prevWeekEnd = getWeekEnd(subWeeks(currentDate, 1));
    onRangeChange({ startDate: prevWeekStart, endDate: prevWeekEnd });
  };
  
  // Navigate to next week (only up to current week)
  const goToNextWeek = () => {
    const nextWeekStart = getWeekStart(addWeeks(currentDate, 1));
    
    // Don't allow navigating to future weeks
    if (isAfter(nextWeekStart, currentWeekStart)) {
      return;
    }
    
    setCurrentDate(addWeeks(currentDate, 1));
    const nextWeekEnd = getWeekEnd(addWeeks(currentDate, 1));
    onRangeChange({ startDate: nextWeekStart, endDate: nextWeekEnd });
  };
  
  // Get days of the week for display
  const weekStart = getWeekStart(currentDate);
  const weekEnd = getWeekEnd(currentDate);
  const daysOfWeek = eachDayOfInterval({ start: weekStart, end: weekEnd });
  
  // Check if a date has a submitted report
  const getReportStatus = (date) => {
    if (!submittedWeeks || submittedWeeks.length === 0) return null;
    
    const matchingWeek = submittedWeeks.find(week => 
      isSameDay(week.startDate, getWeekStart(date))
    );
    
    return matchingWeek ? matchingWeek.status : null;
  };
  
  // Format the week range for display
  const formatWeekRange = (start, end) => {
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };
  
  // Check if we're viewing the current week
  const isCurrentWeek = isSameDay(weekStart, currentWeekStart);
  
  // Check if date is within selected range
  const isDateInSelectedRange = (date) => {
    return isWithinInterval(date, { 
      start: selectedRange.startDate, 
      end: selectedRange.endDate 
    });
  };
  
  return (
    <div className="report-calendar">
      <div className="calendar-header">
        <h2>Select Week</h2>
        <div className="calendar-navigation">
          <button 
            className="nav-button"
            onClick={goToPreviousWeek}
          >
            <FaChevronLeft style={{ color: '#000' }} />
          </button>
          
          <div className="current-range">
            <FaCalendarAlt className="calendar-icon" style={{ color: '#000' }} />
            <span>{formatWeekRange(weekStart, weekEnd)}</span>
          </div>
          
          <button 
            className="nav-button"
            onClick={goToNextWeek}
            disabled={isCurrentWeek}
          >
            <FaChevronRight style={{ color: '#000' }} />
          </button>
        </div>
      </div>
      
      <div className="calendar-body">
        <div className="week-days">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
            <div key={index} className="weekday-label">{day}</div>
          ))}
        </div>
        
        <div className="days-grid">
          {daysOfWeek.map((day, index) => {
            const reportStatus = getReportStatus(day);
            const isSelected = isDateInSelectedRange(day);
            
            return (
              <div 
                key={index} 
                className={`day-cell ${isSelected ? 'selected' : ''} ${reportStatus ? reportStatus : ''}`}
              >
                <span className="day-number">{format(day, 'd')}</span>
                
                {reportStatus && (
                  <div className="status-indicator" title={`Report ${reportStatus}`}>
                    {reportStatus === 'reviewed' ? '✓' : '•'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="status-legend">
        <div className="legend-item">
          <span className="legend-marker pending"></span>
          <span className="legend-label">Pending Review</span>
        </div>
        <div className="legend-item">
          <span className="legend-marker reviewed"></span>
          <span className="legend-label">Reviewed</span>
        </div>
      </div>
    </div>
  );
};

export default ReportCalendar; 